import { ChangeDetectorRef, Component, ElementRef, Inject, Injector, ViewChild, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ButtonActionsTypeEnum } from '@trendbuild/trend-z-api';
import { AbstractComponent, Attendance, AudioService, Channel, Company, Contact, FirebaseService, MessageScheduled, MessageScheduledService, MessageScheduledStatusEnum, MessageTypeEnum, Traduct, UploadTypeEnum, User } from 'lib-trend-core';
import { BehaviorSubject } from 'rxjs';

@Component({
    selector: 'message-schedule',
    // templateUrl: './chat-message-schedule.html',
    templateUrl: './chat-message-schedule.v2.component.html',
    styleUrls: ['./chat-message-schedule.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChatMessageScheduleComponent extends AbstractComponent {
  private loadingSpinnerSubject = new BehaviorSubject<boolean>(false);
  loadingSpinner$ = this.loadingSpinnerSubject.asObservable();
  file!: File;
  form: FormGroup;
  private message: MessageScheduled;
  contentType: string = ''
  attachment: any;
  intervalId!: any;

  @ViewChild('messageTextarea') messageTextarea!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('audioContainer', { static: false }) audioContainer!: ElementRef;

  showContainerEmoji: boolean = false;
  translator!: Traduct;

  //AUDIO
  isRecording!: boolean;
  attendance: Attendance;
  isLoading: boolean = false;
  audioUrl!: string | null;
  isPreview: boolean = false;
  recorder!: MediaRecorder;
  stream!: MediaStream;
  recordingTime: number = 0;
  isCancel!: boolean;
  audioChunks: Blob[] = [];
  audioFile!: File | undefined;
  isRecordingInProgress: boolean = false;

  hourErrorMessage: string = '';
  ButtonActionsTypeEnum = ButtonActionsTypeEnum;

  responseTypes = [
    { value: 'TEXT', label: 'Texto/Imagem' },
    { value: 'BUTTONS', label: 'Botões' }
  ];

  constructor(
    private cdr: ChangeDetectorRef,
    injector: Injector,
    @Inject(MAT_DIALOG_DATA) public data: { attendance?: Attendance; message?: MessageScheduled },
    private fb: FormBuilder,
    private messageScheduledService: MessageScheduledService,
    private firebaseService: FirebaseService,
    private audioService: AudioService,
    public _matDialogRef: MatDialogRef<MessageScheduled>,
  ) {
    super(injector);
    this.message = data.message
    this.form = this.fb.group({
      scheduledDay: ['', Validators.required],
      scheduledHour: ['', Validators.required],
      message: ['',],
      typeResponse: ['TEXT'],
      title: [null],
      footer: [null],
      buttonActions: this.fb.array([]),
      attachment: [null],
    });
  }

  ngOnInit(): void {
    this.isNew = !this.data?.message;

    if (!this.isNew) {
      const msg = this.data.message as any;
      const { scheduledDate, scheduledTime, caption, content, contentType, title, footer } = msg;
      const buttonActions = msg.buttonActions;

      this.form.patchValue({
        scheduledDay: scheduledDate,
        scheduledHour: scheduledTime,
        message: caption || content,
        title: title || '',
        footer: footer || ''
      });

      this.attachment = content;
      this.contentType = contentType;
  
      if (caption === "" || (contentType === "audio/mpeg" && caption === null))  {
        this.form.patchValue({ message: "" });
      }
      if (buttonActions?.length > 0) {
        this.form.get('typeResponse').setValue('BUTTONS');
        this.initButtonActions(buttonActions);
      }
    }

    this.form.get('scheduledHour').valueChanges.subscribe(value => {
      this.validateHour(value);
    });

    this.form.get('typeResponse').valueChanges.subscribe(value => {
      if (value === 'BUTTONS') {
        if (this.getButtonActionsFormArray().length === 0) {
          this.addButtonAction();
        }
      } else {
        this.clearButtonFormArray();
      }

      this.cdr.detectChanges();
    });
  }

  validateHour(value: string): void {
    this.hourErrorMessage = '';
    
    if (!value) {
      return;
    }
    
    if (value.length === 4 && !value.includes(':')) {
      value = value.substring(0, 2) + ':' + value.substring(2);
    }
    
    const hourParts = value.split(':');
    
    if (hourParts.length !== 2) {
      this.hourErrorMessage = 'Formato inválido, use HH:MM (exemplo: 08:00)';
      return;
    }
    
    const hours = parseInt(hourParts[0], 10);
    const minutes = parseInt(hourParts[1], 10);
    
    if (isNaN(hours) || isNaN(minutes)) {
      this.hourErrorMessage = 'Formato inválido, use HH:MM (exemplo: 08:00)';
      return;
    }
    
    if (hours < 0 || hours > 23) {
      this.hourErrorMessage = 'A hora deve estar entre 00:00 e 23:59';
      return;
    }
    
    if (minutes < 0 || minutes > 59) {
      this.hourErrorMessage = 'Os minutos devem estar entre 00 e 59';
      return;
    }
  }

  saveMessageScheduled(): void {
    if (this.isRecordingInProgress) {
      return;
    }

    if (this.form.invalid) {
      this.alertService.warning(this.t('attendance.chatSchedule.fillRequiredFields'));
      return;
    }

    const scheduledDay = this.form.get('scheduledDay').value;
    let scheduledHour = this.form.get('scheduledHour').value?.replace(/^(\d{2})(\d{2})$/, '$1:$2');

    if (!scheduledDay || !scheduledHour) {
      this.alertService.error(this.t('attendance.chatSchedule.scheduledDateTimeRequired'));
      return;
    }

    let scheduledHourHour = scheduledHour.replace(/^(\d{2})(\d{2})$/, '$1:$2');
    const validHourPatternHour = /^([0-9]|0[0-9]|1[0-9]|2[0-3]):([0-9]|[0-5][0-9])$/;

    if (!validHourPatternHour.test(scheduledHourHour)) {
      this.alertService.error(this.t('attendance.chatSchedule.validHourFormat'));
      return;
    }

    const [hourPart, minutePart] = scheduledHourHour.split(':');
    scheduledHourHour = `${hourPart.padStart(2, '0')}:${minutePart.padStart(2, '0')}`;

    const [hour, minute] = scheduledHourHour.split(':').map(Number);
    const scheduledDateTime = new Date(scheduledDay);
    scheduledDateTime.setHours(hour, minute, 0, 0);

    const currentTime = new Date();
    currentTime.setMinutes(currentTime.getMinutes() + 2);

    if (scheduledDateTime < currentTime) {
      this.alertService.error(this.t('attendance.chatSchedule.scheduledDateTimeMinTwoMinutes'));
      return;
    }

    const typeResponse = this.form.get('typeResponse')?.value;
    const message = this.form.get('message')?.value;
    const title = this.form.get('title')?.value;
    const footer = this.form.get('footer')?.value;

    let contentType: string;

    if (typeResponse === 'BUTTONS') {
      contentType = MessageTypeEnum.button;
    } else if (this.file) {
      contentType = this.file.type;
    } else if (this.contentType) {
      contentType = this.contentType;
    } else {
      contentType = MessageTypeEnum.text;
    }

    if (typeResponse === 'BUTTONS') {
      if (!message?.trim()) {
        this.alertService.warning(this.t('attendance.chatSchedule.fillMessageBeforeSaving'));
        return;
      }

      const buttonActions = this.getButtonActionsFormArray().getRawValue();
      if (buttonActions.length === 0) {
        this.alertService.warning(this.t('attendance.chatSchedule.addAtLeastOneButton'));
        return;
      }

      for (const button of buttonActions) {
        if (!button.label?.trim()) {
          this.alertService.warning(this.t('attendance.chatSchedule.fillAllButtonsText'));
          return;
        }

        if (button.type === ButtonActionsTypeEnum.call && !button.phone?.trim()) {
          this.alertService.warning(this.t('attendance.chatSchedule.fillPhoneForCallButtons'));
          return;
        }

        if (button.type === ButtonActionsTypeEnum.url && !button.url?.trim()) {
          this.alertService.warning(this.t('attendance.chatSchedule.fillUrlForLinkButtons'));
          return;
        }
      }
    }

    let messageType: MessageTypeEnum;

    if (contentType.includes('application/pdf')) {
      messageType = MessageTypeEnum.document;
    } else if (contentType.includes('image/')) {
      messageType = MessageTypeEnum.image;
    } else if (contentType.includes('audio/')) {
      messageType = MessageTypeEnum.audio;
    } else if (contentType.includes('video/')) {
      messageType = MessageTypeEnum.video;
    } else if (contentType === MessageTypeEnum.button || typeResponse === 'BUTTONS') {
      messageType = MessageTypeEnum.button;
    } else {
      messageType = MessageTypeEnum.text;
    }

    if (messageType === MessageTypeEnum.text && !message?.trim()) {
      this.alertService.warning(this.t('attendance.chatSchedule.fillMessageBeforeSaving'));
      return;
    }

    const caption = (messageType !== MessageTypeEnum.audio && this.attachment) ? message : null;

    const messageScheduled = <MessageScheduled>(<unknown>{
      content: this.attachment || message,
      caption: caption,
      contact: this.isNew
        ? { _id: this.data.attendance.contact._id }
        : { _id: this.message.contact._id } as Contact,
      channel: this.isNew
        ? { _id: this.data.attendance.channel._id }
        : { _id: this.message.channel._id } as Channel,
      company: { _id: super.getIDCurrentCompany() } as Company,
      attendance: { _id: this.data.attendance._id } as Attendance,
      user: { _id: super.getIDCurrentUser() } as User,
      type: messageType,
      contentType,
      removed: false,
      scheduledDate: scheduledDay,
      scheduledTime: scheduledHour,
      status: MessageScheduledStatusEnum.WAITING,
      metadata: {
        title: title,
        footer: footer,
        buttonActions: typeResponse === 'BUTTONS' ? this.getButtonActionsFormArray().getRawValue() : [],
      }
    });

    const saveObservable = this.isNew
      ? this.messageScheduledService.create(messageScheduled)
      : this.messageScheduledService.update(this.message._id, messageScheduled);

    saveObservable.subscribe({
      next: () => {
        const successMessage = this.isNew
          ? this.t('attendance.chatSchedule.scheduledMessageCreated')
          : this.t('attendance.chatSchedule.scheduledMessageUpdated');
        this.alertService.success(successMessage);
        this._matDialogRef.close();
      },
      error: () => {
        const errorMessage = this.isNew
          ? this.t('attendance.chatSchedule.scheduleMessageError')
          : this.t('attendance.chatSchedule.updateScheduledMessageError');
        this.alertService.error(errorMessage);
      },
    });
  }
  
  applyFormat(format: string) {
    const textarea = this.messageTextarea.nativeElement;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);

    let formattedText = '';

    switch (format) {
      case 'bold':
        formattedText = `*${selectedText}*`;
        break;
      case 'italic':
        formattedText = `_${selectedText}_`;
        break;
      case 'underline':
        formattedText = `~${selectedText}~`;
        break;
      default:
        return;
    }

    textarea.value =
      textarea.value.substring(0, start) +
      formattedText +
      textarea.value.substring(end);

    textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
    textarea.focus();
  }

  isMessageValid(): boolean {
    const messageContent = this.form.get('message')?.value?.trim();
    const attachment = this.form.get('attachment')?.value?.trim();

    if (!this.isNew && !messageContent || !attachment) {
      return false;
    }

    return !(messageContent || attachment || this.message?.content);
  }

  removeAttachment(): void {
    if (this.attachment) {
      this.attachment = null;
      this.file = null;
      this.contentType = 'text';
      this.form.patchValue({ attachment: null });

      this.alertService.success(this.t('attendance.chatSchedule.fileRemovedSuccessfully'));
    } else {
      this.alertService.warning(this.t('attendance.chatSchedule.noFileToRemove'));
    }
  }

  onMidiaChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
  
    const file = input.files[0];
    if (file) {
      this.file = file;
      const mimeType = file.type;
  
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          this.loadingSpinnerSubject.next(true);
          const filename = `${file.name}_${new Date().getTime()}`;
  
          const snapshot = await this.firebaseService.uploadFile(file, filename, UploadTypeEnum.MESSAGE);
          this.loadingSpinnerSubject.next(false);
  
          this.attachment = snapshot.url;
          this.contentType = mimeType;
  
          this.form.patchValue({
            attachment: snapshot.url,
          });
        } catch (error) {
          this.loadingSpinnerSubject.next(false);
          console.error('Erro no upload do arquivo:', error);
        }
      };
      reader.readAsArrayBuffer(file);
    }
  }
  
  //AUDIO
  private startRecording(idAttendance: string): void {
    this.isRecordingInProgress = true;
    if (this.isRecording) return;

    this.recordingTime = 0;
    this.intervalId = setInterval(() => {
      this.recordingTime++;
    }, 1000);

    sessionStorage.removeItem('preview_audio');

    navigator.mediaDevices.getUserMedia({ audio: true })
      .then((stream) => {
        this.isRecording = true;
        this.isCancel = false;
        this.stream = stream;
        this.recorder = new MediaRecorder(this.stream);
        this.audioChunks = [];

        this.recorder.ondataavailable = (event) => {
          if (!this.isCancel) {
            this.audioChunks.push(event.data);
          }
        };

        this.recorder.onstop = async () => {
          if (!this.isCancel) {
            const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
            const filename = `audio.mp3`;
            const newBlob = await this.audioService.convertWebMToMp3(audioBlob);
            this.audioFile = new File([newBlob], filename, { type: 'audio/mpeg' });

            this.audioUrl = URL.createObjectURL(audioBlob);
            if (this.isPreview) {
              const reader = new FileReader();
              reader.onload = () => {
                sessionStorage.setItem(`preview_audio_${idAttendance}`, reader.result as string);
              };
              reader.onerror = (error) => {
                this.alertService.error(this.t('attendance.chatSchedule.audioReadError'));
              };
              reader.readAsDataURL(this.audioFile);

              this.isPreview = false;
            }
          } else {
            this.audioChunks = [];
          }
        };

        this.recorder.start();
      }).catch((err) => this.alertService.error(err));
  }

  private async processFile(file: File): Promise<void> {
    try {
      this.isLoading = true;  
  
      const filename = `${file.name}_${new Date().getTime()}`;
      const mimeType = file.type;
  
      const snapshot = await this.firebaseService.uploadFile(file, filename, UploadTypeEnum.MESSAGE);
  
      this.attachment = snapshot.url; 
      this.contentType = mimeType;
  
      this.form.patchValue({
        attachment: snapshot.url,
      });
    } catch (error) {
      this.isLoading = false;
      console.error('Erro ao fazer o upload do arquivo:', error);
      this.alertService.error(this.t('attendance.chatSchedule.fileUploadError'));
    } finally {
      this.isLoading = false;  
    }
  }
  
  stopRecording(): void {
    this.isRecordingInProgress = false;
    if (this.isRecording) {
      clearInterval(this.intervalId);
      this.recorder.stop();
      this.isRecording = false;
      this.stream?.getTracks().forEach(track => track.stop());

      this.recorder.onstop = async () => {
        if (!this.isCancel) {
          try {
            const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
            const filename = `audio.mp3`;
  
            const newBlob = await this.audioService.convertWebMToMp3(audioBlob);
            this.audioFile = new File([newBlob], filename, { type: 'audio/mpeg' });
  
            this.audioUrl = URL.createObjectURL(audioBlob); 
  
            await this.processFile(this.audioFile);
          } catch (error) {
            console.error('Erro ao processar o áudio:', error);
            this.alertService.error(this.t('attendance.chatSchedule.audioProcessingError'));
          }
        }
      };
    }
  }
  
  cancelRecording(): void {
    if (this.recorder && this.isRecording) {
      this.isCancel = true;
      this.recorder.stop();
      this.isRecording = false;
      this.stream?.getTracks().forEach(track => track.stop());
    }
  }

  toggleRecording(idAttendance: string): void {
    if (this.isRecording) {
      this.isLoading = true;
      this.stopRecording();
      this.audioUrl = sessionStorage.getItem(`preview_audio_${idAttendance}`);
    } else {
      this.isLoading = false;
      this.isPreview = true;
      this.startRecording(idAttendance);
    }
  }

  cancelPreview(idAttendance: string): void {
    this.isLoading = false;
    sessionStorage.removeItem(`preview_audio_${idAttendance}`);
    this.audioUrl = null;
    this.audioFile = undefined;

    if (this.audioContainer) this.audioContainer.nativeElement.innerHTML = '';
  }

  getFormattedTime(): string {
    const minutes: string = Math.floor(this.recordingTime / 60).toString().padStart(2, '0');
    const seconds: string = (this.recordingTime % 60).toString().padStart(2, '0');

    return `${minutes}:${seconds}`;
  }

  shouldDisableTextarea(): boolean {
    return (this.attachment && this.contentType?.includes('audio/')) || 
           this.contentType === 'audio/mpeg' || 
           this.isRecording && !this.isNew;
  }

  getButtonActionsFormArray(): FormArray {
    return this.form.get('buttonActions') as FormArray;
  }

  addButtonAction(): void {
    const buttonGroup = this.fb.group({
      type: [ButtonActionsTypeEnum.reply, Validators.required],
      label: ['', Validators.required],
      phone: [''],
      url: ['']
    });

    buttonGroup.get('type').valueChanges.subscribe(type => {
      if (type === ButtonActionsTypeEnum.call) {
        buttonGroup.get('phone').setValidators([Validators.required]);
        buttonGroup.get('url').clearValidators();
      } else if (type === ButtonActionsTypeEnum.url) {
        buttonGroup.get('url').setValidators([Validators.required]);
        buttonGroup.get('phone').clearValidators();
      } else {
        buttonGroup.get('phone').clearValidators();
        buttonGroup.get('url').clearValidators();
      }
      buttonGroup.get('phone').updateValueAndValidity();
      buttonGroup.get('url').updateValueAndValidity();
    });

    this.getButtonActionsFormArray().push(buttonGroup);
  }

  removeButtonAction(index: number): void {
    this.getButtonActionsFormArray().removeAt(index);
  }

  clearButtonFormArray(): void {
    while (this.getButtonActionsFormArray().length) {
      this.getButtonActionsFormArray().removeAt(0);
    }
  }

  initButtonActions(buttons: any[]): void {
    this.clearButtonFormArray();
    if (buttons && buttons.length) {
      buttons.forEach(button => {
        const buttonGroup = this.fb.group({
          type: [button.type || ButtonActionsTypeEnum.reply, Validators.required],
          label: [button.label || '', Validators.required],
          phone: [button.phone || ''],
          url: [button.url || '']
        });

        buttonGroup.get('type').valueChanges.subscribe(type => {
          if (type === ButtonActionsTypeEnum.call) {
            buttonGroup.get('phone').setValidators([Validators.required]);
            buttonGroup.get('url').clearValidators();
          } else if (type === ButtonActionsTypeEnum.url) {
            buttonGroup.get('url').setValidators([Validators.required]);
            buttonGroup.get('phone').clearValidators();
          } else {
            buttonGroup.get('phone').clearValidators();
            buttonGroup.get('url').clearValidators();
          }
          buttonGroup.get('phone').updateValueAndValidity();
          buttonGroup.get('url').updateValueAndValidity();
        });

        this.getButtonActionsFormArray().push(buttonGroup);
      });
    }
  }

  isTypeText(): boolean {
    return this.form.get('typeResponse')?.value === 'TEXT';
  }

  isTypeButton(): boolean {
    return this.form.get('typeResponse')?.value === 'BUTTONS';
  }

  getButtonGroup(index: number): FormGroup {
  return this.getButtonActionsFormArray().at(index) as FormGroup;
}
}
